// 資訊/規則頁 — 對照 Unity `SlotFramework/Prefab/UI/Base/InfoPanel.prefab` ＋ `Script/UI/Panel/InfoPanel.cs`。
//
// Unity 結構（全部以 1920×1080／1080×1920 設計座標、Expand 縮放）：
//   InfoPanel      全螢幕
//   ├ Mask         全螢幕黑底 α0.8。**沒有 Button**（與 Dialog 不同，點背景不關窗，只能按關閉鈕）
//   ├ Scroll View  全螢幕，vertical only、無捲軸條、Elastic 0.1、Inertia on
//   │  └ Viewport  RectMask2D（上緣內縮 17、下緣內縮 10）
//   │     └ Content  寬 1000、anchor/pivot (0.5,1)、VerticalLayoutGroup（padding 上下 40、spacing 40、UpperCenter）
//   │        ├ InfoGameView（Addressable 動態載入 → Slot026 的 GameRuleInfo.prefab）
//   │        └ InfoCommonView（框架共用段落）
//   └ CloseButton  anchor/pivot (1,1)、aPos (−10,−20)、157×157、localScale 0.8，子圖 84×84 localScale 1.2
//
// 兩段內容分別由 `info/gameRuleSections.ts`（遊戲專屬）與 `info/commonSections.ts`（共用）產生，
// 兩者之間也是根節點的 spacing 40（Unity 的 Content VLG 就是這樣把兩個子 view 接起來的）。
// 開窗時捲回頂端（Unity `_scrollRect.verticalNormalizedPosition = 1`）；Unity 無開關窗動畫（只切 CanvasGroup alpha）。
//
// [已知差異] Unity 的 `Layout_Home`／`Layout_History` 會依平台 HomeUrl／RecordUrl 是否存在隱藏；
//            本專案無平台環境（選單的 Home/Log 本身也還是佔位），故一律顯示。詳見 commonSections.ts 檔頭。

import { Container, Graphics, Sprite } from 'pixi.js'
import type { EventBus } from '../../../framework/events/EventBus'
import { formatMoney } from '../../../framework/core/format'
import {
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import type { GameEventMap } from '../../events/GameEvents'
import { getUiTexture } from '../../assets/UiTextures'
import { ROOT_PADDING, ROOT_SPACING, type InfoBlock } from './info/infoBlocks'
import { gameRuleSections } from './info/gameRuleSections'
import { commonSections, type CommonSectionData } from './info/commonSections'
import { ScrollColumn } from './info/ScrollColumn'
import { reportModalOpen } from './modalGate'

/** Viewport 的上下內縮（由 prefab 的 stretch offsets 換算：offsetMax.y −17、offsetMin.y +10）。 */
const VIEWPORT_INSET_TOP = 17
const VIEWPORT_INSET_BOTTOM = 10

/** 關閉鈕（anchor/pivot 右上、aPos (−10,−20)、157×157 縮放 0.8，子圖 84×84 再縮放 1.2）。 */
const CLOSE_MARGIN_X = 10
const CLOSE_MARGIN_Y = 20
const CLOSE_SIZE = 157
const CLOSE_SCALE = 0.8
const CLOSE_ICON_SIZE = 84
const CLOSE_ICON_SCALE = 1.2

export class InfoPanel extends Container implements Resizable {
  private readonly backdrop = new Graphics()
  private readonly scroll = new ScrollColumn()
  private readonly closeCircle = new Sprite(getUiTexture('btnCircle'))
  private readonly closeIcon = new Sprite(getUiTexture('btnClose'))

  private screenW = 0
  private screenH = 0
  private orientation: Orientation = 'landscape'

  private blocks: InfoBlock[] = []
  /** 目前套用到共用段落的動態值；變動時需重建那段內容。 */
  private data: CommonSectionData = { currencyCode: '', rtp: '', minBet: '', maxBet: '' }

  constructor(private readonly bus: EventBus<GameEventMap>) {
    super()
    this.visible = false
    this.eventMode = 'static'

    // Unity 的 Mask 只是黑底：吃掉點擊（避免穿透到盤面）但不關窗。
    this.backdrop.eventMode = 'static'

    this.closeCircle.anchor.set(1, 0)
    this.closeIcon.anchor.set(0.5)
    this.closeCircle.eventMode = 'static'
    this.closeCircle.cursor = 'pointer'
    this.closeCircle.on('pointertap', () => this.hide())
    // ⚠️ X 圖必須排除在命中測試外：PixiJS v8 的 hitTestRecursive 對 `passive` 子節點會短路
    //    （幾何上蓋住該點就回傳空陣列並中止搜尋），使 hit target 變成本容器；底下的兄弟節點
    //    `closeCircle` 永遠輪不到，按鈕會整個失效（點圓框沒被 X 蓋到的邊緣才有反應）。
    this.closeIcon.eventMode = 'none'

    this.addChild(this.backdrop, this.scroll, this.closeCircle, this.closeIcon)

    this.bus.on('infoClicked', () => this.show())
    this.bus.on('betConfigChanged', ({ options }) => {
      const min = options[0]
      const max = options[options.length - 1]
      if (min === undefined || max === undefined) return
      this.applyData({ minBet: formatMoney(min), maxBet: formatMoney(max) })
    })
  }

  /** 由 main.ts 在取得後端 GetGameInfo 之後注入（幣別代碼與 ClientOptions.RTP[0]）。 */
  setServerInfo(currencyCode: string, rtp: string): void {
    this.applyData({ currencyCode, rtp })
  }

  show(): void {
    this.visible = true
    reportModalOpen(this, true)
    this.scroll.reset()
    this.layout()
  }

  hide(): void {
    this.visible = false
    reportModalOpen(this, false)
  }

  get isOpen(): boolean {
    return this.visible
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.screenW = width
    this.screenH = height
    this.orientation = orientation
    this.layout()
  }

  private applyData(patch: Partial<CommonSectionData>): void {
    const next = { ...this.data, ...patch }
    if (
      next.currencyCode === this.data.currencyCode &&
      next.rtp === this.data.rtp &&
      next.minBet === this.data.minBet &&
      next.maxBet === this.data.maxBet
    ) {
      return
    }
    this.data = next
    // 內容含動態文字，改變時整份重建（開窗頻率低、重建成本可忽略）。
    this.blocks = []
    if (this.visible) this.layout()
  }

  /** 首次顯示或動態值變動後才建構內容（避免開機就付出建立數百個 Text 的成本）。 */
  private ensureBlocks(): void {
    if (this.blocks.length > 0) return
    this.blocks = [...gameRuleSections(), ...commonSections(this.data)]
    for (const block of this.blocks) this.scroll.content.addChild(block.node)
  }

  private layout(): void {
    if (this.screenW === 0 || this.screenH === 0) return
    this.backdrop.clear().rect(0, 0, this.screenW, this.screenH).fill({ color: 0x000000, alpha: 0.8 })
    if (!this.visible) return

    const s =
      this.orientation === 'portrait'
        ? unityScalePortrait(this.screenW, this.screenH)
        : unityScaleLandscape(this.screenW, this.screenH)

    const viewTop = VIEWPORT_INSET_TOP * s
    const viewHeight = this.screenH - (VIEWPORT_INSET_TOP + VIEWPORT_INSET_BOTTOM) * s
    this.scroll.setViewport(0, viewTop, this.screenW, viewHeight)

    this.ensureBlocks()
    // 內容座標相對「內容頂端 0」；ScrollColumn 只搬動 content.y，故這裡用內容區域自己的座標系。
    const centerX = this.screenW / 2
    let cursor = ROOT_PADDING * s
    for (const block of this.blocks) {
      cursor += block.layout(s, centerX, cursor) + ROOT_SPACING * s
    }
    // 最後一段後面不留 spacing，改成 padding-bottom。
    const contentHeight = cursor - ROOT_SPACING * s + ROOT_PADDING * s
    this.scroll.setContentHeight(contentHeight)

    const circle = CLOSE_SIZE * CLOSE_SCALE * s
    const right = this.screenW - CLOSE_MARGIN_X * s
    const top = CLOSE_MARGIN_Y * s
    this.closeCircle.setSize(circle, circle)
    this.closeCircle.position.set(right, top)
    const icon = CLOSE_ICON_SIZE * CLOSE_ICON_SCALE * CLOSE_SCALE * s
    this.closeIcon.setSize(icon, icon)
    this.closeIcon.position.set(right - circle / 2, top + circle / 2)
  }
}
